import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Spinner from '../components/Spinner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import {
  RiCalendarCheckLine,
  RiMoneyDollarCircleLine,
  RiStarLine,
  RiLeafLine,
  RiTeamLine,
  RiUserHeartLine,
  RiChatSmile2Line,
} from 'react-icons/ri';

interface TopService {
  nom: string;
  count: number;
}

interface Stats {
  sessions_du_jour: number;
  revenus_estimes: number;
  taux_satisfaction: number | null;
  top_services: TopService[];
}

const KpiCard: React.FC<{
  title: string;
  value: string;
  icon: React.ElementType;
  color: string;
  bg: string;
}> = ({ title, value, icon: Icon, color, bg }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-6 flex items-center gap-5 shadow-sm">
    <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`text-2xl ${color}`} />
    </div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{title}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  </div>
);

const QuickLink: React.FC<{
  to: string;
  label: string;
  desc: string;
  icon: React.ElementType;
  color: string;
  bg: string;
}> = ({ to, label, desc, icon: Icon, color, bg }) => (
  <Link
    to={to}
    className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4 shadow-sm hover:shadow-md hover:border-brand-200 transition-all group"
  >
    <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`text-xl ${color}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-900 text-sm group-hover:text-brand-600 transition-colors">{label}</p>
      <p className="text-xs text-gray-500 truncate">{desc}</p>
    </div>
    <span className="text-gray-300 group-hover:text-brand-400 transition-colors text-lg">→</span>
  </Link>
);

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chartRes] = await Promise.all([
          api.get('/stats/'),
          api.get('/stats/charts'),
        ]);
        setStats(statsRes.data);
        setChartData(chartRes.data);
      } catch {
        setError('Impossible de charger les statistiques.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Spinner />;
  if (error) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-red-500 text-sm">{error}</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-sm text-gray-500 mt-1">Vue d'ensemble de l'activité du salon</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <KpiCard
          title="Sessions aujourd'hui"
          value={String(stats?.sessions_du_jour ?? 0)}
          icon={RiCalendarCheckLine}
          color="text-blue-500"
          bg="bg-blue-50"
        />
        <KpiCard
          title="Revenus estimés"
          value={`${(stats?.revenus_estimes ?? 0).toLocaleString()} F`}
          icon={RiMoneyDollarCircleLine}
          color="text-green-500"
          bg="bg-green-50"
        />
        <KpiCard
          title="Taux de satisfaction"
          value={stats?.taux_satisfaction != null ? `${stats.taux_satisfaction} / 5` : 'N/A'}
          icon={RiStarLine}
          color="text-yellow-500"
          bg="bg-yellow-50"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-800 mb-5">Top soins demandés</h2>
          {stats?.top_services && stats.top_services.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.top_services} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="nom" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Bar dataKey="count" fill="#d14f8b" name="Demandes" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-800 mb-5">Revenus — 7 derniers jours</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#d14f8b" name="Revenus (F)" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="sessions" stroke="#6366f1" name="Sessions" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Accès rapides</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickLink
            to="/admin/catalogue"
            label="Catalogue"
            desc="Catégories et services"
            icon={RiLeafLine}
            color="text-brand-500"
            bg="bg-brand-50"
          />
          <QuickLink
            to="/admin/employes"
            label="Employés"
            desc="Gérer l'équipe"
            icon={RiTeamLine}
            color="text-indigo-500"
            bg="bg-indigo-50"
          />
          <QuickLink
            to="/admin/clients"
            label="Clientes"
            desc="Historique et fidélité"
            icon={RiUserHeartLine}
            color="text-emerald-500"
            bg="bg-emerald-50"
          />
          <QuickLink
            to="/admin/feedbacks"
            label="Retours"
            desc="Avis et réponses"
            icon={RiChatSmile2Line}
            color="text-orange-500"
            bg="bg-orange-50"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
